import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { useAuthContext, useLocaleContext } from '@/providers/Global'
import {
  ensureWebPushRegistration,
  type IWebPushMessagePayload,
  isWebPushNotificationMessage,
} from '@/services/WebPush'

const WEB_PUSH_SYNC_INTERVAL_MS = 5 * 60 * 1000

export const WebPushRegistration: React.FC = () => {
  const Auth = useAuthContext()
  const Locale = useLocaleContext()
  const requestedPermissionForUsername = useRef<string | undefined>(undefined)

  const username = Auth.active?.username

  const syncRegistration = useCallback(
    (requestPermission: boolean) => {
      if (!username) {
        return
      }

      void ensureWebPushRegistration(Locale.active, { requestPermission }).catch(() => undefined)
    },
    [Locale.active, username],
  )

  const syncVisibleRegistration = useCallback(() => {
    if (document.visibilityState === 'visible') {
      syncRegistration(false)
    }
  }, [syncRegistration])

  useEffect(() => {
    if (!username) {
      requestedPermissionForUsername.current = undefined

      return
    }

    const requestPermission = requestedPermissionForUsername.current !== username

    requestedPermissionForUsername.current = username
    syncRegistration(requestPermission)

    const interval = window.setInterval(() => {
      syncVisibleRegistration()
    }, WEB_PUSH_SYNC_INTERVAL_MS)

    const handleFocus = () => {
      syncRegistration(false)
    }

    const handleOnline = () => {
      syncRegistration(false)
    }

    const handleVisibilityChange = () => {
      syncVisibleRegistration()
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('online', handleOnline)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('online', handleOnline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [syncRegistration, syncVisibleRegistration, username])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isWebPushNotificationMessage(event.data) || document.visibilityState !== 'visible') {
        return
      }

      const payload = event.data.payload

      toast.info(renderPushToast(payload), {
        onClick: () => openPushTarget(payload.url),
      })
    }

    navigator.serviceWorker?.addEventListener('message', handleMessage)

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage)
    }
  }, [])

  return null
}

function renderPushToast(payload: IWebPushMessagePayload) {
  return (
    <>
      <strong>{payload.title || 'EAPC'}</strong>
      {payload.body && <div>{payload.body}</div>}
    </>
  )
}

function openPushTarget(rawUrl?: string) {
  if (!rawUrl) {
    return
  }

  try {
    const url = new URL(rawUrl, window.location.origin)

    if (url.origin === window.location.origin) {
      window.location.assign(url.href)
    }
  } catch {
    return
  }
}
